import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Chip,
  Divider,
  alpha,
  useTheme,
  Avatar,
  InputAdornment,
} from "@mui/material";
import {
  Event,
  Work,
  Terrain,
  Construction,
  Search,
  Science,
  Assessment,
  Layers,
  Straighten,
} from "@mui/icons-material";
import api from "../../api";
import { todayLocalISO } from "../../utils/dateUtils";
import PageShell, { PanelCard } from "../../components/common/PageShell";
import JobDailyChart from "../../components/admin/JobDailyChart";

// Bölüm Başlığı (Mevcut Temaya Uygun)
const SectionHeader = ({ title, icon, color }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        p: 1,
        borderRadius: "10px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Box>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 800, color: "text.primary", fontFamily: "Montserrat" }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.1) }} />
  </Stack>
);

// Ustteki toplam kutulari (hepsi ayni kaliptan)
const OzetKutusu = ({ etiket, deger, renk }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: "1px solid #eaecf0",
      bgcolor: "#fff",
      borderLeft: `4px solid ${renk}`,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "0.62rem",
        display: "block",
      }}
    >
      {etiket}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, color: renk, mt: 0.25 }}>
      {deger}
    </Typography>
  </Box>
);

export default function DailyReportPage() {
  const theme = useTheme();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    todayLocalISO(),
  );
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  // Calismanin gun gun uretim serisi (grafik + genel toplam)
  const [seri, setSeri] = useState(null);
  const [seriYukleniyor, setSeriYukleniyor] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await api.get("/admin/jobs");
        if (res.data.success) setJobs(res.data.jobs);
      } catch (error) {
        console.error("İşler yüklenemedi:", error);
      }
    };
    fetchJobs();
  }, []);

  // Ustte bir calisma secilince o calismanin tum gunlerini cekiyoruz.
  useEffect(() => {
    if (!selectedJob) {
      setSeri(null);
      return;
    }
    let iptal = false;
    setSeriYukleniyor(true);
    api
      .get(`/admin/job-daily-series/${selectedJob.id}`)
      .then((res) => {
        if (!iptal && res.data.success) setSeri(res.data.data);
      })
      .catch((err) => console.error("Seri hatasi:", err))
      .finally(() => !iptal && setSeriYukleniyor(false));
    return () => {
      iptal = true;
    };
  }, [selectedJob]);

  const fetchStats = async () => {
    if (!selectedJob) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/job-daily-stats/${selectedJob.id}?date=${selectedDate}`,
      );
      if (res.data.success) setStats(res.data.data);
    } catch (error) {
      console.error("Rapor hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Günlük Üretim Raporu"
      subtitle="Saha verilerini ve katman bazlı numune detaylarını inceleyin."
      icon={<Assessment />}
    >

      {/* FİLTRELEME */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: "20px",
          border: "1px solid #eaecf0",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <Autocomplete
          fullWidth
          options={jobs}
          getOptionLabel={(option) => option.job_name}
          value={selectedJob}
          onChange={(e, nv) => setSelectedJob(nv)}
          renderInput={(params) => (
            <TextField {...params} label="Çalışma (Job)" size="small" />
          )}
          sx={{ flex: 2 }}
        />
        <TextField
          fullWidth
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={fetchStats}
          disabled={loading || !selectedJob}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Search />
            )
          }
          sx={{ px: 4, height: 40, borderRadius: "12px", fontWeight: "bold" }}
        >
          SORGULA
        </Button>
      </Paper>

      {/* GENEL TOPLAM + GUN GUN GRAFIK */}
      {selectedJob && (
        <PanelCard>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ mb: 2, gap: 1 }}
          >
            <Typography variant="subtitle2" fontWeight={800}>
              {selectedJob.job_name} — Genel Üretim
            </Typography>
            {seri?.gunSayisi > 0 && (
              <Typography variant="caption" color="text.secondary">
                {seri.ilkGun?.split("-").reverse().join(".")} –{" "}
                {seri.sonGun?.split("-").reverse().join(".")} arası{" "}
                {seri.gunSayisi} çalışma günü
              </Typography>
            )}
          </Stack>

          {seriYukleniyor ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Üretim geçmişi yükleniyor...
              </Typography>
            </Stack>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    md: "repeat(5, 1fr)",
                  },
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <OzetKutusu
                  etiket="Toplam Profil"
                  deger={seri?.toplam?.profil ?? 0}
                  renk={theme.palette.success.main}
                />
                <OzetKutusu
                  etiket="Toplam Sonda"
                  deger={seri?.toplam?.sonda ?? 0}
                  renk={theme.palette.error.main}
                />
                <OzetKutusu
                  etiket="Profil Numunesi"
                  deger={seri?.toplam?.profilNumune ?? 0}
                  renk={theme.palette.primary.main}
                />
                <OzetKutusu
                  etiket="Sonda Katmanı"
                  deger={seri?.toplam?.sondaNumune ?? 0}
                  renk={theme.palette.secondary.main}
                />
                <OzetKutusu
                  etiket="Günlük Ortalama"
                  deger={
                    seri?.gunSayisi
                      ? (
                          (seri.toplam.profil + seri.toplam.sonda) /
                          seri.gunSayisi
                        ).toFixed(1)
                      : 0
                  }
                  renk={theme.palette.warning.main}
                />
              </Box>

              <JobDailyChart
                seri={seri?.seri || []}
                seciliGun={selectedDate}
                onGunSec={(gun) => setSelectedDate(gun)}
              />
            </>
          )}
        </PanelCard>
      )}

      {/* VERİ ALANI */}
      {stats && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 3,
          }}
        >
          {/* PROFİLLER VE NUMUNELERİ */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: "24px",
              border: "1px solid #eaecf0",
            }}
          >
            <SectionHeader
              title="Profiller ve Katmanlar"
              icon={<Terrain />}
              color={theme.palette.success.main}
            />

            <Stack spacing={2.5}>
              {stats.profiles?.length > 0 ? (
                stats.profiles.map((p, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: "#fcfcfd",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Box>
                        <Typography
                          fontWeight={800}
                          color="success.dark"
                          variant="body1"
                        >
                          Profil: {p.no}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            p.agent_name,
                            p.created_at
                              ? new Date(p.created_at).toLocaleTimeString(
                                  "tr-TR",
                                  { hour: "2-digit", minute: "2-digit" },
                                )
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${p.horizons?.length || 0} Numune`}
                        color="success"
                        sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                      />
                    </Stack>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {p.horizons?.map((h, hIdx) => (
                        <Chip
                          key={hIdx}
                          size="small"
                          icon={
                            <Science sx={{ fontSize: "14px !important" }} />
                          }
                          label={`${h.derinlik_min}-${h.derinlik_max} cm (${h.tekstur || "N/A"})`}
                          sx={{
                            bgcolor: "#fff",
                            border: "1px solid #e8f5e9",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  Profil kaydı bulunamadı.
                </Typography>
              )}
            </Stack>
          </Paper>

          {/* SONDALAR VE NUMUNELERİ */}
          <Paper
            sx={{
              flex: 1,
              p: 3,
              borderRadius: "24px",
              border: "1px solid #eaecf0",
            }}
          >
            <SectionHeader
              title="Sondalar ve Katmanlar"
              icon={<Construction />}
              color={theme.palette.error.main}
            />

            <Stack spacing={2.5}>
              {stats.sondas?.length > 0 ? (
                stats.sondas.map((s, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: "#fcfcfd",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Box>
                        <Typography
                          fontWeight={800}
                          color="error.dark"
                          variant="body1"
                        >
                          Sonda: {s.no}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            s.agent_name,
                            s.created_at
                              ? new Date(s.created_at).toLocaleTimeString(
                                  "tr-TR",
                                  { hour: "2-digit", minute: "2-digit" },
                                )
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${s.horizons?.length || 0} Numune`}
                        color="error"
                        sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                      />
                    </Stack>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {s.horizons?.map((h, hIdx) => (
                        <Chip
                          key={hIdx}
                          size="small"
                          icon={<Layers sx={{ fontSize: "14px !important" }} />}
                          label={`${h.derinlik_min}-${h.derinlik_max} cm (${h.tekstur || "N/A"})`}
                          sx={{
                            bgcolor: "#fff",
                            border: "1px solid #ffebee",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  Sonda kaydı bulunamadı.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>
      )}
    </PageShell>
  );
}
